import React from "react";
import { recordAudioStreamStop, recordNewAudioStreamStart } from "../../functions/recordNewAudioStream";
import { streamLatestSavedAudio } from "../../functions/streamLatestSavedAudio";
import { OnAudioDataAvailable } from "../../models/AudioStream.interface";
import IconButton from "../theme_reuse/IconButton";
import AudioFileLoader from "./AudioFileLoader";
import "./AudioControlBar.css";
import { ICON_CLOSE, ICON_RECORD, ICON_STOP } from "../../constants/iconConstants";
import { AppContextType, AppLoadingState, HelpPageName } from "../../AppContext";

/** Summary of info gleaned from audio processing: The latest frequency information. */
export interface LatestAudioInfo {
  // histogramData: Uint8Array; // This output is specific to the JS implementation (AnalyserNode produces a histogram)
  sampleRate: number;
  partialRecordingBlob: Blob; // This output is only used by the webworker implementation
}
interface AudioControlBarProps {
  onLatestAudioInfoChange: (args: LatestAudioInfo) => void;
  /** Either because recording was stopped, or a playing stream ended. */
  onStreamComplete: () => void;
  onAudioFeedStateChange: (state: AudioFeedStatus) => void;
  onHistoryReset: () => void;
  onAudioSeek: (integerTime: number) => void;
  autoPauseResumeIncrement: number;
}
interface AudioControlBarState {
  mediaRecorder?: MediaRecorder;
  recordingChunks: Array<Blob>;
  savedAudioBlob?: Blob;
  savedAudioUrl?: string;
  audioState: AudioState;
  playbackState: PlaybackState;
  sampleRate: number;
  fileUploadResetTicker: number;
  recoveryAudioSeekTime: number;
  recoveryAudioUrl?: string;
  recorderInstanceId: number;
  debouncedSeekTime: null | number;
  preventNextDebouncedSeek: boolean;
  isFirstVisit: boolean;
  firstVisitHighlightCounter: number;
}

/** Simplified status for the parent */
export enum AudioFeedStatus {
  ACTIVE,
  INACTIVE,
}

enum AudioState {
  NO_DATA,
  ERROR_RECOVERY_UNMOUNT,
  ERROR_RECOVERY_REMOUNT,
  RECORDING,
  SAVED,
  SAVED_AND_CONNECTED,
}

enum PlaybackState {
  NO_DATA,
  PLAYING,
  PAUSED,
  STOPPED,
}

/** For init and reset */
function buildInitialState(overrides: Partial<AudioControlBarState> = {}): AudioControlBarState {
  return {
    mediaRecorder: undefined,
    audioState: AudioState.NO_DATA,
    playbackState: PlaybackState.NO_DATA,
    recordingChunks: [],
    savedAudioBlob: undefined,
    savedAudioUrl: undefined,
    sampleRate: 0,
    fileUploadResetTicker: 0,
    recoveryAudioSeekTime: 0,
    recoveryAudioUrl: undefined,
    recorderInstanceId: 0,
    debouncedSeekTime: null,
    preventNextDebouncedSeek: false,
    isFirstVisit: false,
    firstVisitHighlightCounter: 0,
    ...overrides,
  };
}

/**
 * Encapsulates the transformation of audio input to a frequency histogram.
 *
 * This includes handling audio recording, audio upload, streaming either from microphone or recorded audio.
 */
export default class AudioControlBar extends React.Component<AudioControlBarProps, AudioControlBarState> {
  declare context: React.ContextType<typeof AppContextType>;
  constructor(props: AudioControlBarProps) {
    super(props);
    this.state = buildInitialState();
  }

  audioRef = React.createRef<HTMLMediaElement>();
  onDataAvailable: OnAudioDataAvailable = async ({ pcmBlob, rawBlob, sampleRate }) => {
    const { audioState, playbackState } = this.state;
    const { onLatestAudioInfoChange } = this.props;
    if (audioState === AudioState.RECORDING) {
      this.setState({ recordingChunks: [...this.state.recordingChunks, pcmBlob], sampleRate });
    }
    if (audioState === AudioState.RECORDING || playbackState === PlaybackState.PLAYING) {
      if (this.context.browser.streamFormat === "WEBM_PCM" && pcmBlob) {
        onLatestAudioInfoChange({ sampleRate, partialRecordingBlob: pcmBlob });
      } else if (this.context.browser.streamFormat === "RAW" && rawBlob) {
        onLatestAudioInfoChange({ sampleRate, partialRecordingBlob: rawBlob });
      }
    }
  };

  componentDidMount() {
    const isFirstVisit = this.context.showHelpPopup && this.context.helpPageName === HelpPageName.Welcome;
    if (isFirstVisit) {
      this.setState({ isFirstVisit });
    }
  }

  async componentDidUpdate(oldProps: AudioControlBarProps, oldState: AudioControlBarState) {
    this.checkCompUpdateErrorRecoverySequence(oldProps);
    this.checkCompUpdateInitFirstVisitUx();
  }

  checkCompUpdateInitFirstVisitUx = () => {
    if (this.state.isFirstVisit && !this.context.showHelpPopup) {
      this.setState({ isFirstVisit: false, firstVisitHighlightCounter: 7 });
      this.setTimeoutDecrementVisitHighlight();
    }
  };

  setTimeoutDecrementVisitHighlight = () => {
    setTimeout(() => {
      const { firstVisitHighlightCounter } = this.state;
      if (firstVisitHighlightCounter > 0) {
        this.setState({
          firstVisitHighlightCounter: firstVisitHighlightCounter - 1,
        });
        this.setTimeoutDecrementVisitHighlight(); // recurse until 0
      }
    }, 120);
  };

  checkCompUpdateErrorRecoverySequence = (oldProps: AudioControlBarProps) => {
    // Attempt to recover from a corrupted stream by re-initializing the audio component
    if (
      // State0: stream failure detected, begin unmount/remount process
      oldProps.autoPauseResumeIncrement !== this.props.autoPauseResumeIncrement &&
      this.state.audioState === AudioState.SAVED_AND_CONNECTED // record-mode recovery is different
    ) {
      this.audioRef.current!.pause();
      const recoveryAudioSeekTime = Math.floor(this.audioRef.current!.currentTime);
      console.log("Web pause and clear out mediaRecorder instance, t=" + recoveryAudioSeekTime);
      try {
        this.state.mediaRecorder?.stop();
      } catch (e: any) {
        console.log("Failed to stop the mediaRecorder", e);
      }
      // State1: unmount the audio component and clean up the media recorder
      this.setState({
        savedAudioUrl: "", // trigger unmount
        recoveryAudioSeekTime,
        recoveryAudioUrl: this.state.savedAudioUrl,
        audioState: AudioState.ERROR_RECOVERY_UNMOUNT,
        playbackState: PlaybackState.PAUSED,
        mediaRecorder: undefined, // so that the audio src change triggers re-creation
      });
    }
    // State2: If there's a recoveryUrl and no savedAudioUrl, remount.
    else if (this.state.audioState === AudioState.ERROR_RECOVERY_UNMOUNT && this.state.recoveryAudioUrl) {
      console.log("Web remounting audio");
      this.props.onAudioSeek(this.state.recoveryAudioSeekTime); // tell WASM the new timestamp
      this.setState({
        savedAudioUrl: this.state.recoveryAudioUrl, // trigger remount
        recoveryAudioUrl: undefined,
        audioState: AudioState.ERROR_RECOVERY_REMOUNT,
        preventNextDebouncedSeek: true,
      });
      // State3: if we've remounted, hit play to create new stream from the point of failure
    } else if (this.state.audioState === AudioState.ERROR_RECOVERY_REMOUNT) {
      if (this.audioRef.current) {
        console.log("Web restarting the remounted audio, at t=" + this.state.recoveryAudioSeekTime);
        this.audioRef.current.currentTime = this.state.recoveryAudioSeekTime;
        this.audioRef.current.play().catch((_) => {
          /* don't care */
        });
        this.setState({
          audioState: AudioState.SAVED,
        });
      }
    }
  };

  handleStartRecording = async () => {
    this.handleDeleteRecordedAudio(); // if a prior recording was replayed, clean up the replay stream + reset file upload state
    const recorderInstanceId = this.state.recorderInstanceId + 1;
    const { streamFormat } = this.context.browser;
    const { error, mediaRecorder } = await recordNewAudioStreamStart(this.onDataAvailable, { id: recorderInstanceId, streamFormat });
    if (error) {
      this.context.dispatch({ type: "AddToErrorList", payload: [error] });
    }
    if (mediaRecorder) {
      this.setState({ mediaRecorder, audioState: AudioState.RECORDING, recorderInstanceId });
      this.props.onAudioFeedStateChange(AudioFeedStatus.ACTIVE);
    }
  };

  handleStopRecording = async () => {
    const { recordingChunks, mediaRecorder } = this.state;
    await recordAudioStreamStop(mediaRecorder);
    const savedAudioBlob = new Blob(recordingChunks, { type: mediaRecorder!.mimeType });
    const savedAudioUrl = window.URL.createObjectURL(savedAudioBlob);
    // possible bug: not sending the last moment of the audio
    this.setState({ audioState: AudioState.SAVED, savedAudioBlob, savedAudioUrl });
    this.props.onAudioFeedStateChange(AudioFeedStatus.INACTIVE);
    this.props.onStreamComplete();
  };

  handleAudioFileUpload = (savedAudioBlob: Blob) => {
    const savedAudioUrl = window.URL.createObjectURL(savedAudioBlob);
    this.setState({ mediaRecorder: undefined, recordingChunks: [], savedAudioBlob, savedAudioUrl, audioState: AudioState.SAVED });
  };

  handleStartPlaying = async (e: any) => {
    console.log(" we see a play event!");
    const { audioState } = this.state;
    const audioElement = this.audioRef.current;
    this.releaseDebouncedSeekChange();
    // console.log("startplaying", e);
    if (audioElement && audioState !== AudioState.SAVED_AND_CONNECTED) {
      // if we're replaying the same audio, don't repeat stream setup.
      // Downside: The old onDataAvailable fades out slowly; so histogram data is polluted by whatever was playing before.
      const recorderInstanceId = this.state.recorderInstanceId + 1;
      const { streamFormat } = this.context.browser;
      const { error, mediaRecorder } = await streamLatestSavedAudio(audioElement, this.onDataAvailable, {
        id: recorderInstanceId,
        streamFormat,
      });
      if (error) {
        console.error("Error creating mediaRecorder", error);
      }
      this.setState({
        mediaRecorder,
        audioState: AudioState.SAVED_AND_CONNECTED,
        playbackState: PlaybackState.PLAYING,
        recorderInstanceId,
      });
    } else {
      this.setState({ playbackState: PlaybackState.PLAYING });
    }
    this.props.onAudioFeedStateChange(AudioFeedStatus.ACTIVE);
  };

  handleStreamEnded = async (e: any) => {
    this.setState({ playbackState: PlaybackState.STOPPED });
    this.props.onAudioFeedStateChange(AudioFeedStatus.INACTIVE);
    // The "end of stream" from the perspective of the audio element does not mean
    // we should end the stream between JS and WASM.
    // The user could technically rewind and re-do sections at this point.
    // this.props.onStreamComplete(); // so don't clean up until they change the recording/file.
  };

  handlePausePlaying = async (e: any) => {
    this.setState({ playbackState: PlaybackState.PAUSED });
    this.props.onAudioFeedStateChange(AudioFeedStatus.INACTIVE);
    // this.props.onAudioPause();
  };

  handleSeeked = (e: React.ChangeEvent<HTMLAudioElement>) => {
    // Give the WASM process the latest integer timestamp.
    if (this.audioRef.current) {
      if (Number.isInteger(e.target.currentTime)) {
        // console.log("integer seek time=" + e.target.currentTime + "  and ref time=" + this.audioRef.current.currentTime);
        if (e.target.currentTime === 0) {
          this.audioRef.current.pause();
        }
        this.setState({ debouncedSeekTime: e.target.currentTime });
      } else {
        // re-trigger the handler with an integer value for time
        // console.log("raw seek time", e.target.currentTime);
        this.audioRef.current!.currentTime = Math.floor(e.target.currentTime);
      }
    }
  };

  /**
   * Seeked event fires many times while dragging (it is not supposed to),
   * and <audio> swallows various click events.  Relying on play event after seeking to debounce.
   */
  releaseDebouncedSeekChange = () => {
    if (this.state.preventNextDebouncedSeek) {
      // After automatic recovery, do not re-broadcast seek time information - it's already done.
      this.setState({
        preventNextDebouncedSeek: false,
        debouncedSeekTime: null,
      });
      return;
    }
    if (this.state.debouncedSeekTime !== null) {
      console.log("debounced seek to =" + this.state.debouncedSeekTime);
      this.props.onAudioSeek(this.state.debouncedSeekTime);
      if (this.state.debouncedSeekTime === 0) {
        // flush out any pending data (restart the 500s window at the seek time)
        // Only at t=0 because (1) alignment/consistency matters the most when there's a restart (2) at times other than T=0, this tends to cause stream parsing errors.
        this.state.mediaRecorder!.requestData();
      }
      this.audioRef.current!.play().catch((_) => {
        /* don't care */
      });
      this.setState({ debouncedSeekTime: null });
    }
  };

  handleDeleteRecordedAudio = async () => {
    if (this.state.mediaRecorder && this.state.savedAudioUrl) {
      await recordAudioStreamStop(this.state.mediaRecorder);
    }
    const fileUploadResetTicker = this.state.fileUploadResetTicker + 1;
    this.setState(buildInitialState({ fileUploadResetTicker }));
    this.props.onAudioFeedStateChange(AudioFeedStatus.INACTIVE);
    this.props.onStreamComplete();
    this.props.onHistoryReset();
  };

  render() {
    const isAppLoading = this.context.loading !== AppLoadingState.READY;
    const { savedAudioUrl, audioState, fileUploadResetTicker, firstVisitHighlightCounter } = this.state;
    const fileLoadedClass = savedAudioUrl ? "audio-file-loaded" : "";
    // console.log("rendering renderAudioControlBar, savedAudioUrl=" + savedAudioUrl);
    return (
      <div className={`audio-control-bar ${fileLoadedClass}`}>
        <div className="audio-control-bar-line-1">
          <AudioFileLoader disabled={isAppLoading} onUpload={this.handleAudioFileUpload} resetTicker={fileUploadResetTicker} />
          <IconButton
            title="Start Recording"
            icon={ICON_RECORD}
            onClick={this.handleStartRecording}
            disabled={isAppLoading || audioState === AudioState.RECORDING}
            highlight={firstVisitHighlightCounter % 2 === 1}
          />
          <IconButton icon={ICON_STOP} onClick={this.handleStopRecording} disabled={audioState !== AudioState.RECORDING} />
        </div>
        {savedAudioUrl && (
          <div className="audio-control-bar-line-2">
            <audio
              controls
              controlsList="nofullscreen nodownload noremoteplayback noplaybackrate"
              autoPlay={false}
              src={savedAudioUrl}
              ref={this.audioRef}
              onPlay={this.handleStartPlaying}
              onPause={this.handlePausePlaying}
              onEnded={this.handleStreamEnded}
              onSeeked={this.handleSeeked}
            />
            <IconButton icon={ICON_CLOSE} title="Delete Recording and Clear History" onClick={this.handleDeleteRecordedAudio} />
          </div>
        )}
      </div>
    );
  }
}

AudioControlBar.contextType = AppContextType;
